import React, { useMemo, useState } from 'react';
import { TextField } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import IconSave from '@mui/icons-material/SaveAltOutlined';
import IconDelete from '@mui/icons-material/Delete';
import IconAdd from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import ConfirmPopup from '../ConfirmPopup/ConfirmPopup';
import Toast from '../../components/support/Toast';
import { DataProviderInstance } from '../../api/DataProvider';
import {
  AMBIENT_MAX, AMBIENT_MIN, CUSTOM_SITE, THERMOGRAPHY_REGIONS, formatTemp, formatThreshold, isAmbientInRange, parseTemp, previousOf,
} from '../../pages/appointment/modules/thermography/thermography';
import { fieldKey, formToReadings, newCustomRow, readingsToForm, regionsWithValues } from './thermographyForm';
import { CustomSiteRow, RegionSection } from './ThermographyFields';
import styles from './ThermographyPopup.module.css';

const DATE_FORMAT = 'YYYY-MM-DD';
const NOTES_MAX = 2000;

/** Data padrão: a do atendimento (nunca no futuro), senão hoje. */
const defaultDate = (appointmentDate) => {
  const today = dayjs().format(DATE_FORMAT);
  if (!appointmentDate || !dayjs(appointmentDate).isValid()) return today;
  const date = dayjs(appointmentDate).format(DATE_FORMAT);
  return date > today ? today : date;
};

/** Valores numéricos da avaliação anterior por campo, para as dicas. */
const previousValues = (record) => {
  const map = {};
  ((record && record.readings) || []).forEach((reading) => {
    if (reading && reading.site !== CUSTOM_SITE && reading.value != null) map[fieldKey(reading.site, reading.side)] = reading.value;
  });
  return map;
};

/**
 * Registro ou edição da avaliação termográfica do atendimento: data,
 * temperatura da sala, temperaturas por local (regiões recolhíveis, com delta
 * T ao vivo nos pares), locais livres e observações. Abre já com as regiões
 * usadas na avaliação anterior e mostra os valores anteriores como dica.
 *
 * `history`: avaliações do paciente, mais recente primeiro.
 */
function ThermographyPopup({ patientId, appointmentId, appointmentDate, entry, history, OnHide }) {
  const { SetPatientThermography, DeletePatientThermography } = DataProviderInstance();

  const previous = useMemo(
    () => (entry ? previousOf(history || [], entry) : (history || [])[0] || null),
    [entry, history]
  );
  const initial = useMemo(() => readingsToForm(entry && entry.readings), [entry]);
  const hints = useMemo(() => previousValues(previous), [previous]);

  const [date, setDate] = useState(entry ? dayjs(entry.measuredAt).format(DATE_FORMAT) : defaultDate(appointmentDate));
  const [ambient, setAmbient] = useState(entry && entry.ambientTemp != null ? formatTemp(entry.ambientTemp) : '');
  const [values, setValues] = useState(initial.values);
  const [customs, setCustoms] = useState(initial.customs);
  const [notes, setNotes] = useState((entry && entry.notes) || '');
  const [expanded, setExpanded] = useState(() => {
    const keys = regionsWithValues(initial.values);
    return keys.size > 0 ? keys : regionsWithValues(readingsToForm(previous && previous.readings).values);
  });
  const [saving, setSaving] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });

  const showToast = (message, severity = 'error') => setToast({ open: true, message, severity });

  function toggleRegion(key) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  const setValue = (key, text) => setValues((current) => ({ ...current, [key]: text }));
  const updateCustom = (uid, patch) => setCustoms((rows) => rows.map((row) => (row.uid === uid ? { ...row, ...patch } : row)));
  const removeCustom = (uid) => setCustoms((rows) => rows.filter((row) => row.uid !== uid));

  function saveHandler() {
    if (saving) return;
    if (!date) {
      showToast('Informe a data da avaliação.');
      return;
    }
    if (ambient.trim() !== '' && !isAmbientInRange(parseTemp(ambient))) {
      showToast(`A temperatura da sala deve ficar entre ${formatTemp(AMBIENT_MIN)} e ${formatTemp(AMBIENT_MAX)} °C.`);
      return;
    }
    const result = formToReadings(values, customs);
    if (result.error) {
      showToast(result.error);
      return;
    }

    setSaving(true);
    const payload = {
      readings: result.readings,
      measuredAt: date,
      ambientTemp: ambient.trim() === '' ? '' : parseTemp(ambient),
      notes: notes.trim(),
    };
    SetPatientThermography(patientId, entry ? entry.appointmentId : appointmentId, payload, (response) => {
      setSaving(false);
      if (response && response.status === 'SUCCESS') {
        OnHide(true);
      } else {
        showToast(response?.error || 'Não foi possível salvar a avaliação.');
      }
    });
  }

  function deleteHandler() {
    DeletePatientThermography(entry.id, (response) => {
      if (response && response.status === 'SUCCESS') {
        OnHide(true);
      } else {
        showToast('Não foi possível excluir a avaliação.');
      }
    });
  }

  const content = (
    <div className={styles.container}>
      <div className={styles.header}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Data da avaliação"
            value={date ? dayjs(date) : null}
            format="DD/MM/YYYY"
            disableFuture={true}
            onChange={(value) => setDate(value && value.isValid() ? value.format(DATE_FORMAT) : null)}
            slotProps={{ textField: { size: 'small' } }}
          />
        </LocalizationProvider>
        <TextField
          size="small"
          label="Temperatura da sala (°C)"
          value={ambient}
          onChange={(e) => setAmbient(e.target.value.replace(/[^\d.,]/g, '').slice(0, 5))}
          error={ambient.trim() !== '' && !isAmbientInRange(parseTemp(ambient))}
          inputProps={{ inputMode: 'decimal' }}
          className={styles.ambientField}
        />
      </div>

      <span className={styles.hint}>
        Temperaturas de 20,0 a 42,0 °C. Nos pares, ΔT = direito menos esquerdo; acima de {formatThreshold()} °C aparece destacado.
        {previous && ` Dicas em cinza: avaliação de ${dayjs(previous.measuredAt).format('DD/MM/YYYY')}.`}
      </span>

      <div className={styles.regions}>
        {THERMOGRAPHY_REGIONS.map((region) => (
          <RegionSection
            key={region.key}
            region={region}
            values={values}
            previous={hints}
            expanded={expanded.has(region.key)}
            onToggle={() => toggleRegion(region.key)}
            onChange={setValue}
          />
        ))}
      </div>

      <div className={styles.customs}>
        <span className={styles.sectionLabel}>Outros locais</span>
        {customs.map((row) => (
          <CustomSiteRow
            key={row.uid}
            row={row}
            onChange={(patch) => updateCustom(row.uid, patch)}
            onRemove={() => removeCustom(row.uid)}
          />
        ))}
        <div>
          <CustomButton
            variant="outlined"
            style="secondary"
            label="Adicionar local"
            icon={<IconAdd />}
            onClick={() => setCustoms((rows) => [...rows, newCustomRow()])}
          />
        </div>
      </div>

      <TextField
        size="small"
        label="Observações (opcional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value.slice(0, NOTES_MAX))}
        multiline
        minRows={2}
      />

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      {entry && (
        <CustomButton
          variant="contained"
          style="error"
          label="Excluir"
          icon={<IconDelete />}
          onClick={() => setShowConfirmDelete(true)}
          enabled={!saving}
        />
      )}

      <CustomButton
        variant="outlined"
        style="secondary"
        label="Cancelar"
        onClick={() => OnHide(false)}
        enabled={!saving}
      />

      <CustomButton
        style="primary"
        variant="contained"
        label={entry ? 'Atualizar' : 'Registrar'}
        icon={<IconSave />}
        onClick={saveHandler}
        loading={saving}
        enabled={!saving}
      />

      {showConfirmDelete && (
        <ConfirmPopup
          Visibility={showConfirmDelete}
          Title="Confirmar exclusão"
          Description="Tem certeza que deseja excluir esta avaliação termográfica?"
          ConfirmLabel="Excluir"
          OnConfirm={deleteHandler}
          OnHide={() => setShowConfirmDelete(false)}
        />
      )}
    </div>
  );

  return (
    <PopupBase
      Title={entry ? 'Editar termografia' : 'Nova termografia'}
      Content={content}
      Footer={footer}
      OnClose={() => OnHide(false)}
      maxWidth="md"
    />
  );
}

export default ThermographyPopup;
